<div class="container mx-auto p-4 bg-[#F5F7FA] min-h-screen">
  <!-- En-tête -->
  <div class="mb-6">
    <h1 class="text-3xl font-bold text-[#1E293B] mb-2">{{ t('admin.subscription.title') }}</h1>
    <p class="text-gray-600">{{ t('admin.subscription.description') }}</p>
  </div>

  <!-- Barre de recherche et actions -->
  <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
    <!-- Recherche -->
    <div class="relative w-full lg:w-96">
      <input type="text" [(ngModel)]="searchTerm" (input)="searchPlans()"
        [placeholder]="t('admin.subscription.searchPlaceholder')"
        class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C01] focus:border-transparent" />
      <svg xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>

    <!-- Boutons d'action -->
    <div class="flex gap-3 w-full lg:w-auto">
      <button (click)="exportPlans()"
        class="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
        <span class="text-gray-700 font-medium">{{ t('admin.subscription.export') }}</span>
      </button>

      <button (click)="createPlan()"
        class="bg-[#FF5C01] hover:bg-[#E54F00] text-white px-6 py-3 rounded-lg flex items-center justify-center transition-colors duration-200 font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        {{ t('admin.subscription.create') }}
      </button>
    </div>
  </div>

  <!-- Loader -->
  <div *ngIf="isLoading" class="flex items-center justify-center py-12">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5C01]"></div>
  </div>

  <!-- Tableau des plans -->
  <div *ngIf="!isLoading" class="bg-white rounded-lg shadow-sm overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{ t('admin.subscription.table.name') }}
            </th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{ t('admin.subscription.table.cost') }}
            </th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{
              t('admin.subscription.table.installments') }}</th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{
              t('admin.subscription.table.projectLimit') }}</th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{
              t('admin.subscription.table.discount') }}</th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{ t('admin.subscription.table.status')
              }}</th>
            <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">{{ t('admin.subscription.table.actions')
              }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr *ngFor="let plan of paginatedPlans" class="hover:bg-gray-50 transition-colors">
            <!-- Nom du plan -->
            <td class="px-6 py-4">
              <div>
                <div class="text-sm font-semibold text-gray-900">{{ plan.label }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ plan.name }}</div>
                <!-- <div class="text-sm text-gray-500 mt-1">{{ plan.description }}</div> -->
              </div>
            </td>

            <!-- Coût total -->
            <td class="px-6 py-4">
              <span class="text-sm font-semibold text-gray-900">{{ formatAmount(plan.totalCost) }}</span>
            </td>

            <!-- Échéances -->
            <td class="px-6 py-4">
              <span class="text-sm text-gray-700">{{ plan.installmentCount }}</span>
            </td>

            <!-- Limite projets -->
            <td class="px-6 py-4">
              <span class="text-sm text-gray-700">{{ getProjectLimit(plan) }}</span>
            </td>

            <!-- Remise annuelle -->
            <td class="px-6 py-4">
              <span class="text-sm text-gray-700">{{ plan.yearlyDiscountRate }}%</span>
            </td>

            <!-- Statut -->
            <td class="px-6 py-4">
              <span class="px-3 py-1 rounded-full text-xs font-medium" [ngClass]="getStatutClass(plan.active)">
                {{ getStatutText(plan.active) }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <!-- Voir -->
                <button (click)="viewPlan(plan)" class="text-gray-600 hover:text-gray-900 transition-colors"
                  [title]="t('admin.subscription.action.view')">
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.9994 6.6884C13.808 6.68239 15.5815 7.18688 17.1162 8.14387C18.6509 9.10087 19.8843 10.4715 20.6748 12.0982C19.0518 15.413 15.7371 17.5081 11.9994 17.5081C8.26165 17.5081 4.9469 15.413 3.32395 12.0982C4.11439 10.4715 5.34785 9.10087 6.88252 8.14387C8.41719 7.18688 10.1908 6.68239 11.9994 6.6884ZM11.9994 4.72119C7.08133 4.72119 2.88133 7.78021 1.17969 12.0982C2.88133 16.4163 7.08133 19.4753 11.9994 19.4753C16.9174 19.4753 21.1174 16.4163 22.819 12.0982C21.1174 7.78021 16.9174 4.72119 11.9994 4.72119ZM11.9994 9.63922C12.6515 9.63922 13.277 9.8983 13.7381 10.3595C14.1993 10.8206 14.4584 11.4461 14.4584 12.0982C14.4584 12.7504 14.1993 13.3759 13.7381 13.837C13.277 14.2982 12.6515 14.5573 11.9994 14.5573C11.3472 14.5573 10.7217 14.2982 10.2606 13.837C9.79942 13.3759 9.54034 12.7504 9.54034 12.0982C9.54034 11.4461 9.79942 10.8206 10.2606 10.3595C10.7217 9.8983 11.3472 9.63922 11.9994 9.63922ZM11.9994 7.67201C9.56001 7.67201 7.57313 9.6589 7.57313 12.0982C7.57313 14.5376 9.56001 16.5245 11.9994 16.5245C14.4387 16.5245 16.4256 14.5376 16.4256 12.0982C16.4256 9.6589 14.4387 7.67201 11.9994 7.67201Z"
                      fill="#4B4848" />
                  </svg>
                </button>
                <!-- Modifier -->
                <button (click)="editPlan(plan)" class="text-[#FF5C01] hover:text-[#E54F00] transition-colors"
                  [title]="t('admin.subscription.action.edit')">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M2.5 17.5V13.9583L13.5 2.97917C13.6667 2.82639 13.8508 2.70833 14.0525 2.625C14.2542 2.54167 14.4658 2.5 14.6875 2.5C14.9092 2.5 15.1244 2.54167 15.3333 2.625C15.5422 2.70833 15.7228 2.83333 15.875 3L17.0208 4.16667C17.1875 4.31944 17.3092 4.5 17.3858 4.70833C17.4625 4.91667 17.5006 5.125 17.5 5.33333C17.5 5.55556 17.4619 5.7675 17.3858 5.96917C17.3097 6.17083 17.1881 6.35472 17.0208 6.52083L6.04167 17.5H2.5ZM14.6667 6.5L15.8333 5.33333L14.6667 4.16667L13.5 5.33333L14.6667 6.5Z"
                      fill="#F39C12" />
                  </svg>
                </button>
                <!-- Activer/Désactiver -->
                <!-- <button (click)="togglePlanStatus(plan)"
                  [class]="plan.active ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-700'"
                  class="transition-colors" [title]="plan.active ? 'Désactiver' : 'Activer'">

                  <svg *ngIf="plan.active" width="20" height="20" viewBox="0 0 20 20" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M10 0C11.3261 0 12.5979 0.526784 13.5355 1.46447C14.4732 2.40215 15 3.67392 15 5V7.5625C15.7059 7.70658 16.3402 8.09011 16.7958 8.64819C17.2514 9.20626 17.5001 9.90459 17.5 10.625V16.875C17.5 17.7038 17.1708 18.4987 16.5847 19.0847C15.9987 19.6708 15.2038 20 14.375 20H5.625C4.7962 20 4.00134 19.6708 3.41529 19.0847C2.82924 18.4987 2.5 17.7038 2.5 16.875V10.625C2.49985 9.90459 2.74861 9.20626 3.20418 8.64819C3.65975 8.09011 4.29415 7.70658 5 7.5625V5C5 3.67392 5.52678 2.40215 6.46447 1.46447C7.40215 0.526784 8.67392 0 10 0ZM5.625 8.75C5.12772 8.75 4.65081 8.94754 4.29917 9.29917C3.94754 9.65081 3.75 10.1277 3.75 10.625V16.875C3.75 17.3723 3.94754 17.8492 4.29917 18.2008C4.65081 18.5525 5.12772 18.75 5.625 18.75H14.375C14.8723 18.75 15.3492 18.5525 15.7008 18.2008C16.0525 17.8492 16.25 17.3723 16.25 16.875V10.625C16.25 10.1277 16.0525 9.65081 15.7008 9.29917C15.3492 8.94754 14.8723 8.75 14.375 8.75H5.625ZM10 1.25C9.00544 1.25 8.05161 1.64509 7.34835 2.34835C6.64509 3.05161 6.25 4.00544 6.25 5V7.5H13.75V5C13.75 4.00544 13.3549 3.05161 12.6517 2.34835C11.9484 1.64509 10.9946 1.25 10 1.25Z"
                      fill="#FF5C02" />
                  </svg>

                  <svg *ngIf="!plan.active" width="20" height="20" viewBox="0 0 20 20" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_4891_11525)">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M15 0C16.3261 0 17.5979 0.526784 18.5355 1.46447C19.4732 2.40215 20 3.67392 20 5V8.125H18.75V5C18.75 4.00544 18.3549 3.05161 17.6517 2.34835C16.9484 1.64509 15.9946 1.25 15 1.25C14.0054 1.25 13.0516 1.64509 12.3483 2.34835C11.6451 3.05161 11.25 4.00544 11.25 5V7.5H11.875C12.2854 7.5 12.6917 7.58083 13.0709 7.73788C13.45 7.89492 13.7945 8.12511 14.0847 8.41529C14.3749 8.70547 14.6051 9.04997 14.7621 9.42911C14.9192 9.80826 15 10.2146 15 10.625V16.875C15 17.2854 14.9192 17.6917 14.7621 18.0709C14.6051 18.45 14.3749 18.7945 14.0847 19.0847C13.7945 19.3749 13.45 19.6051 13.0709 19.7621C12.6917 19.9192 12.2854 20 11.875 20H3.125C2.2962 20 1.50134 19.6708 0.915291 19.0847C0.32924 18.4987 0 17.7038 0 16.875L0 10.625C0 9.7962 0.32924 9.00134 0.915291 8.41529C1.50134 7.82924 2.2962 7.5 3.125 7.5H10V5C10 3.67392 10.5268 2.40215 11.4645 1.46447C12.4021 0.526784 13.6739 0 15 0ZM3.125 8.75C2.62772 8.75 2.15081 8.94754 1.79917 9.29917C1.44754 9.65081 1.25 10.1277 1.25 10.625V16.875C1.25 17.3723 1.44754 17.8492 1.79917 18.2008C2.15081 18.5525 2.62772 18.75 3.125 18.75H11.875C12.3723 18.75 12.8492 18.5525 13.2008 18.2008C13.5525 17.8492 13.75 17.3723 13.75 16.875V10.625C13.75 10.1277 13.5525 9.65081 13.2008 9.29917C12.8492 8.94754 12.3723 8.75 11.875 8.75H3.125Z"
                        fill="#00B894" />
                    </g>
                    <defs>
                      <clipPath id="clip0_4891_11525">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>

                </button> -->



              </div>
            </td>
          </tr>

          <tr *ngIf="filteredPlans.length === 0">
            <td colspan="7" class="px-6 py-8 text-center text-gray-500">
              {{ t('admin.subscription.empty') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="totalPages > 1" class="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
      <div class="text-sm text-gray-600">
        {{ t('admin.subscription.pagination.showing') }} {{ (currentPage - 1) * pageSize + 1 }} {{
        t('admin.subscription.pagination.to') }} {{ Math.min(currentPage * pageSize, totalResults) }} {{
        t('admin.subscription.pagination.of') }}
        {{ totalResults }} {{ t('admin.subscription.pagination.results') }}
      </div>
      <div class="flex items-center gap-2">
        <button (click)="previousPage()" [disabled]="currentPage === 1" [class.opacity-50]="currentPage === 1"
          [class.cursor-not-allowed]="currentPage === 1"
          class="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300">
          {{ t('admin.subscription.pagination.previous') }}
        </button>

        <button *ngFor="let page of [].constructor(Math.min(totalPages, 5)); let i = index" (click)="goToPage(i + 1)"
          [class]="(i + 1) === currentPage ? 'bg-[#FF5C01] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
          class="w-10 h-10 rounded-lg flex items-center justify-center font-medium transition-colors border border-gray-300">
          {{ i + 1 }}
        </button>

        <button (click)="nextPage()" [disabled]="currentPage === totalPages"
          [class.opacity-50]="currentPage === totalPages" [class.cursor-not-allowed]="currentPage === totalPages"
          class="px-4 py-2 bg-white text-[#FF5C01] rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300">
          {{ t('admin.subscription.pagination.next') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal de confirmation de suppression -->
  <div *ngIf="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h3 class="text-xl font-bold text-gray-900 mb-4">{{ t('admin.subscription.modal.delete.title') }}</h3>
      {{ t('admin.subscription.modal.delete.message', { plan: planToDelete?.label }) }}

      <div class="flex justify-end gap-3">
        <button (click)="cancelDelete()" [disabled]="isDeleting"
          class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
          {{ t('stock.modals.order.cancel') }}
        </button>
        <button (click)="deletePlan()" [disabled]="isDeleting"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center">
          <span *ngIf="!isDeleting">{{ t('admin.subscription.modal.delete.confirm') }}</span>
          <span *ngIf="isDeleting" class="flex items-center">
            <svg class="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none">
              </circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            {{ t('admin.subscription.modal.delete.deleting') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</div>
<!-- 🆕 NOTIFICATION DE SUCCÈS -->
<div *ngIf="showNotification" class="fixed top-4 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-[60] animate-slideIn">

  <!-- Notification Désactivation -->
  <div *ngIf="notificationType === 'deactivated'" class="flex items-start gap-3">
    <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
      <svg class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd" />
      </svg>
    </div>
    <div class="flex-1">
      <p class="text-sm text-gray-900">
        <span
          [innerHTML]="t('admin.subscription.notification.deactivated', { plan: selectedPlanForAction?.label })"></span>
      </p>
    </div>
  </div>

  <!-- Notification Activation -->
  <div *ngIf="notificationType === 'activated'" class="flex items-start gap-3">
    <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
      <svg class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd" />
      </svg>
    </div>
    <div class="flex-1">
      <p class="text-sm text-gray-900">
        <span
          [innerHTML]="t('admin.subscription.notification.activated', { plan: selectedPlanForAction?.label })"></span>
      </p>
    </div>
  </div>
</div>

<!-- 🆕 MODAL DE CONFIRMATION ACTIVER/DÉSACTIVER -->
<div *ngIf="showToggleModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg shadow-xl p-6 w-96 animate-fadeIn">

    <!-- Icône d'alerte -->
    <div class="flex justify-center mb-4">
      <svg width="95" height="95" viewBox="0 0 95 95" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M51.2422 58.166L43.8203 58.166L43.8203 28.4785L51.2422 28.4785L51.2422 58.166Z" fill="#EFBD8E" />
        <path
          d="M47.5 65.291C45.3685 65.291 43.6406 67.0189 43.6406 69.1504C43.6406 71.2819 45.3685 73.0098 47.5 73.0098C49.6315 73.0098 51.3594 71.2819 51.3594 69.1504C51.3594 67.0189 49.6315 65.291 47.5 65.291Z"
          fill="#EFBD8E" />
        <path
          d="M47.5 94.9776C59.6972 95.3703 71.5436 90.595 80.446 81.6968C89.3483 72.7986 94.5815 60.5025 95 47.5C94.5815 34.4975 89.3483 22.2014 80.446 13.3032C71.5436 4.40504 59.6972 -0.370316 47.5 0.0224457C35.3028 -0.370316 23.4564 4.40504 14.554 13.3032C5.65166 22.2014 0.418472 34.4975 0 47.5C0.418472 60.5025 5.65166 72.7986 14.554 81.6968C23.4564 90.595 35.3028 95.3703 47.5 94.9776ZM47.5 7.93537C57.7293 7.54133 67.6886 11.4827 75.1994 18.8972C82.7101 26.3117 87.1609 36.5959 87.5781 47.5C87.1609 58.4041 82.7101 68.6883 75.1994 76.1028C67.6886 83.5174 57.7293 87.4587 47.5 87.0646C37.2707 87.4587 27.3114 83.5174 19.8006 76.1028C12.2899 68.6883 7.83908 58.4041 7.42188 47.5C7.83908 36.5959 12.2899 26.3117 19.8006 18.8972C27.3114 11.4827 37.2707 7.54133 47.5 7.93537Z"
          fill="#EFBD8E" />
      </svg>

    </div>

    <!-- Titre du modal -->
    <h3 class="text-center text-lg font-semibold mb-2" *ngIf="modalAction === 'deactivate'">
      {{ t('admin.subscription.modal.toggle.deactivate.title') }}
    </h3>
    <h3 class="text-center text-lg font-semibold mb-2" *ngIf="modalAction === 'activate'">
      {{ t('admin.subscription.modal.toggle.activate.title') }}
    </h3>

    <!-- Message de confirmation -->
    {{ t('admin.subscription.modal.toggle.deactivate.message', { plan: selectedPlanForAction?.label }) }}

    {{ t('admin.subscription.modal.toggle.activate.message', { plan: selectedPlanForAction?.label }) }}

    <!-- Boutons d'action -->
    <div class="flex gap-3">
      <!-- Bouton Désactiver/Activer -->
      <button (click)="confirmToggleAction()" [disabled]="isLoading"
        [class]="modalAction === 'deactivate' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-900 hover:bg-blue-800'"
        class="flex-1 text-white font-medium py-2.5 px-4 rounded-md transition-colors disabled:opacity-50">
        {{ modalAction === 'deactivate' ? t('admin.subscription.status.inactive') :
        t('admin.subscription.status.active') }}
      </button>

      <!-- Bouton Annuler -->
      <button (click)="cancelToggleAction()" [disabled]="isLoading"
        class="flex-1 bg-white border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50">
        {{ t('stock.modals.order.cancel') }}
      </button>
    </div>